<!--
  ClientActivityStream.vue -- chronological event feed.

  Reads /api/clients/<id>/activity. Renders up to 50 events newest
  first. Event kinds:
    * invitation_sent / invitation_accepted / invitation_revoked
    * approval_* (send/resend/remind/escalate/bypass/approve/
                  request_changes/reject)
    * login

  Each event renders as a one-line entry with relative time. Useful for
  the "who said what when" audit + spot-check use case.
-->
<template>
    <div class="cas" data-testid="client-activity-stream">
        <div v-if="loading" class="cas__loading">Loading activity...</div>
        <div v-else-if="error" class="cas__error">{{ error }}</div>
        <p v-else-if="events.length === 0" class="cas__empty">
            No activity recorded yet.
        </p>
        <ol v-else class="cas__list">
            <li
                v-for="(ev, idx) in events"
                :key="idx"
                class="cas__item"
                :data-testid="`client-activity-event-${idx}`"
            >
                <span class="cas__label">{{ ev.label }}</span>
                <span v-if="ev.detail" class="cas__detail">{{ ev.detail }}</span>
                <span class="cas__time">{{ relTime(ev.at) }}</span>
            </li>
        </ol>
    </div>
</template>

<script>
import api from '@/services/api'

function relTime(iso) {
    if (!iso) return ''
    const t = Date.parse(iso)
    if (Number.isNaN(t)) return ''
    const days = Math.floor((Date.now() - t) / (24 * 60 * 60 * 1000))
    if (days <= 0) {
        const hours = Math.floor((Date.now() - t) / (60 * 60 * 1000))
        if (hours <= 0) return 'just now'
        return hours === 1 ? '1 hour ago' : `${hours} hours ago`
    }
    if (days === 1) return 'yesterday'
    if (days < 7) return `${days} days ago`
    if (days < 30) {
        const weeks = Math.round(days / 7)
        return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`
    }
    const months = Math.round(days / 30)
    return months === 1 ? '1 month ago' : `${months} months ago`
}

export default {
    name: 'ClientActivityStream',
    props: {
        clientId: { type: [Number, String], required: true },
    },
    data() {
        return {
            events: [],
            loading: true,
            error: '',
        }
    },
    watch: {
        clientId() {
            this.load()
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        relTime,
        async load() {
            this.loading = true
            this.error = ''
            try {
                const res = await api.get(`/clients/${this.clientId}/activity`)
                this.events = Array.isArray(res?.data?.events)
                    ? res.data.events
                    : []
            } catch (e) {
                this.error = 'Could not load activity.'
                this.events = []
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
.cas__loading,
.cas__error,
.cas__empty {
    padding: 12px;
    color: var(--tf-fg-2);
    font-size: 13px;
}
.cas__error {
    color: #c0392b;
}
.cas__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.cas__item {
    display: flex;
    align-items: baseline;
    gap: 12px;
    padding: 6px 0;
    border-bottom: 1px solid var(--tf-border, #d0d4dc);
    font-size: 13px;
}
.cas__item:last-child {
    border-bottom: none;
}
.cas__label {
    flex: 0 0 auto;
    font-weight: 500;
    color: var(--tf-fg-1);
}
.cas__detail {
    flex: 1;
    color: var(--tf-fg-2);
    font-family: monospace;
    font-size: 12px;
}
.cas__time {
    color: var(--tf-fg-3, #6b7280);
    font-size: 12px;
}
</style>
